import TitleBar from '../../../components/TitleBar';
import React from 'react';
import { FETCH_EXPOSURE_SUMMARY } from '../queries';
import { useQuery } from '@apollo/client';
import { AntModal, Button } from '../../../components';
import { Footer } from '../../../components/common/Footer';

import { TableColumnsType } from 'antd';

import { EyeOutlined } from '@ant-design/icons';

import './locationSummary.css';
import Filters from '../../../components/Filters/Filters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Summary } from '../../../components/Summary';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils/tableSortingMethods';
import FinancialData from '../../../components/common/FinancialData';
import QueryErrorBoundary from '../../../components/error-handling';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import { baseColumnsLocationSummary } from './exportColumns';
import TitleUpdater from '../../../components/common/TitleUpdater';

export type ExposureSummaryTypes = {
  directRevenueSupported: number;
  indirectRevenueSupported: number;
  locationName: string;
  mitigation: number;
  mitigationPercentage: number;
  netRevenueExposure: number;
  rebuildTime: number;
  totalRevenueSupported: number;
  uuid: string;
  unmitigatedRevenue: number;
  finishedGoodsInventory: number;
  internalMitigation: number;
  externalMitigation: number;
  finishedGoodsInventoryPercentage: number;
  internalMitigationPercentage: number;
  externalMitigationPercentage: number;
};

interface DataReturned {
  exposureSummary: ExposureSummaryTypes[];
}

export default function ExposureSummary() {
  let [searchParams] = useSearchParams();
  const locationUuid = searchParams.get('location');
  const divisionUuid = searchParams.get('division');
  const [modalOpen, setModalOpen] = React.useState(false);
  const [item, setItem] = React.useState<ExposureSummaryTypes>();

  const navigate = useNavigate();

  const { canViewFinancials, selectedTags, isLocation } = useUserContext();

  const { data, loading, error } = useQuery(FETCH_EXPOSURE_SUMMARY, {
    variables: { filter: { locationUuid, divisionUuid } },
  });

  const locationSummaryData = data?.exposureSummary;

  const filteredLocations = getFilteredTags({
    data: locationSummaryData,
    selectedTags,
    condition: isLocation,
    lookUpKey: 'uuid',
  });

  const handleCancel = () => {
    setModalOpen(false);
  };

  const handleClick = (item: ExposureSummaryTypes) => {
    setItem(item);
    setModalOpen(true);
  };

  const customColumns: TableColumnsType<any> = [
    {
      key: 'locationName',
      title: 'Location',
      width: 200,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationName, b.locationName),
      render: ({ locationName }) => (
        <span className="custom-table--column">{locationName}</span>
      ),
    },
    {
      key: 'directRevenueSupported',
      title: 'Direct Revenue Supported',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(
              a.directRevenueSupported,
              b.directRevenueSupported,
            )
        : undefined,
      render: ({ directRevenueSupported }) => (
        <span className="custom-table--column">
          {<FinancialData text={directRevenueSupported} />}
        </span>
      ),
    },
    {
      key: 'indirectRevenueSupported',
      title: 'Indirect Revenue Supported',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(
              a.indirectRevenueSupported,
              b.indirectRevenueSupported,
            )
        : undefined,
      render: ({ indirectRevenueSupported }) => (
        <span className="custom-table--column">
          {<FinancialData text={indirectRevenueSupported} />}
        </span>
      ),
    },
    {
      key: 'totalRevenueSupported',
      title: 'Total Revenue Supported',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(
              a.totalRevenueSupported,
              b.totalRevenueSupported,
            )
        : undefined,
      render: ({ totalRevenueSupported }) => (
        <span className="custom-table--column">
          {<FinancialData text={totalRevenueSupported} />}
        </span>
      ),
    },
    {
      key: 'rebuildTime',
      title: 'Rebuild Time (months)',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.rebuildTime, b.rebuildTime),
      render: ({ rebuildTime }) => (
        <span className="custom-table--column">{rebuildTime}</span>
      ),
    },
    {
      key: 'finishedGoodsInventory',
      title: 'Finished Goods Inventory',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(
              a.finishedGoodsInventory,
              b.finishedGoodsInventory,
            )
        : undefined,
      render: ({ finishedGoodsInventory }) => (
        <span className="custom-table--column">
          {<FinancialData text={finishedGoodsInventory} />}
        </span>
      ),
    },
    {
      key: 'mitigation',
      title: 'Mitigation',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.mitigation, b.mitigation)
        : undefined,
      render: ({ mitigation }) => (
        <span className="custom-table--column">
          {<FinancialData text={mitigation} />}
        </span>
      ),
    },
    {
      key: 'mitigationPercentage',
      title: 'Mitigation %',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.mitigationPercentage,
          b.mitigationPercentage,
        ),
      render: ({ mitigationPercentage }) => (
        <span className="custom-table--column">
          {mitigationPercentage} {'%'}
        </span>
      ),
    },
    {
      key: 'netRevenueExposure',
      title: 'Residual Revenue Exposure',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(
              a.netRevenueExposure,
              b.netRevenueExposure,
            )
        : undefined,
      render: ({ netRevenueExposure }) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={netRevenueExposure} />}
        </span>
      ),
    },
  ];

  const extendActions = (record: any) => {
    return (
      <Button
        key={record.uuid}
        type="edit"
        onClick={() => handleClick(record)}
        className=" border-blue border p-2 rounded-full hover:bg-blue"
      >
        <EyeOutlined className="text-blue hover:text-white" />
      </Button>
    );
  };

  return (
    <div>
      <TitleUpdater title={`Location Exposure Summary`} />
      <TitleBar title="Exposure Summary" subtitle="Location View" />
      <Button
        type="primary"
        className="ml-auto"
        onClick={() => navigate('/exposure-summary/process-areas')}
      >
        Process Area View
      </Button>
      <Filters showFilters={['division', 'location']} />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Location Summary"
          columns={customColumns}
          data={filteredLocations}
          loading={loading}
          extendActions={extendActions}
          customStyles="custom-column-PA--heading custom-table--exposure-summary"
          exportExcelColumns={baseColumnsLocationSummary}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/exposures'} />
      <AntModal
        title="Location Exposure Summary"
        openModal={modalOpen}
        onCancel={handleCancel}
      >
        <Summary
          title={item?.locationName!}
          annualRevenue={item?.totalRevenueSupported!}
          rebuildTime={item?.rebuildTime!}
          totalRevenueExposure={item?.unmitigatedRevenue!}
          totalMitigation={item?.mitigation!}
          totalmitigationPercentage={item?.mitigationPercentage}
          finishedGoodsInventory={item?.finishedGoodsInventory}
          finishedGoodsInventoryPercentage={
            item?.finishedGoodsInventoryPercentage
          }
          internalMitigation={item?.internalMitigation}
          internalMitigationPercentage={item?.internalMitigationPercentage}
          externalMitigation={item?.externalMitigation}
          externalMitigationPercentage={item?.externalMitigationPercentage}
          residualRevenueExposure={item?.netRevenueExposure!}
        />
      </AntModal>
    </div>
  );
}
